import React, { useEffect, useRef } from 'react';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  X, Square, RectangleHorizontal, RectangleVertical, Check, ChevronDown, Send, 
  Sparkles, Image, Images, Video, Type, Code, Grid3X3, Shuffle, ArrowRight, 
  FileText, Calendar 
} from 'lucide-react';
import uploadIcon from '@/assets/upload-icon.svg';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSidebar } from '@/components/ui/sidebar';
import { useVisualsCreationEditor, sortPlatforms } from '@/contexts/VisualsCreationEditorContext';
import { PromptPreviewCarousel } from '@/components/visual-generation/PromptPreviewCarousel';
import { ImageSelectionModal } from '@/components/visual-generation/ImageSelectionModal';
import { ImagePreviewModal } from '@/components/visual-generation/ImagePreviewModal';
import { GenerationResultsGrid } from '@/components/visual-generation/GenerationResultsGrid';
import { ShortcodePopup } from '@/components/visual-generation/ShortcodePopup';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const GlobalVisualsCreationEditor: React.FC = () => {
  const isMobile = useIsMobile();
  const { state: sidebarState } = useSidebar();
  const scrollRef = useRef<HTMLDivElement>(null);
  
  const {
    isOpen,
    closeEditor,
    selectedPromptId,
    customPrompt,
    selectedFiles,
    firstFrameFiles,
    lastFrameFiles,
    uploadingFrame,
    setSelectedFiles,
    setFirstFrameFiles,
    setLastFrameFiles,
    setUploadingFrame,
    isImageModalOpen,
    activeCarouselTab,
    isPreviewModalOpen,
    previewImageUrl,
    isPreviewVideo,
    setIsImageModalOpen,
    setActiveCarouselTab,
    setIsPreviewModalOpen,
    setPreviewImageUrl,
    setIsPreviewVideo,
    selectedSize,
    selectedVisualSize,
    setSelectedSize,
    setSelectedVisualSize,
    selectedModels,
    selectedGenerationType,
    setSelectedGenerationType,
    generationMode,
    maxImages,
    isGenerating,
    progress,
    results,
    showShortcodePopup,
    setGenerationMode,
    setMaxImages,
    setResults,
    setShowShortcodePopup,
    handlePromptSelect,
    handleCustomPromptChange,
    handleModelSelect,
    handleGenerate,
    handleSetAsPreview,
    validModelsForTab,
    sizes,
    sizesLoading,
    aiModels,
  } = useVisualsCreationEditor();

  // Scroll to top when opening
  useEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [isOpen]);

  // Calculate drawer width (sidebar-aware)
  const sidebarWidth = sidebarState === 'expanded' ? '16rem' : '3rem';
  const drawerStyle = isMobile 
    ? { top: '57px' } 
    : { left: sidebarWidth, width: `calc(100% - ${sidebarWidth})` };

  return (
    <>
      <Drawer open={isOpen} onOpenChange={(open) => !open && closeEditor()} direction="right" modal={false}>
        <DrawerContent 
          className="h-full mt-0 rounded-none border-l fixed inset-y-0 right-0 bg-background"
          style={drawerStyle}
        >
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-0 right-2 z-50 h-8 w-8"
            onClick={closeEditor}
          >
            <X className="h-4 w-4" />
          </Button>

          <ScrollArea className="h-full" ref={scrollRef}>
            <div className="container mx-auto px-4 py-6 max-w-7xl">

              {/* Mobile: Icons in Two Rows Above Preview */}
              {isMobile && (
                <div className="mb-4 space-y-2">
                  {/* Row 1: Types + Filters */}
                  <div className="flex justify-center gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setSelectedGenerationType('all')}>
                      <Sparkles className={`h-3.5 w-3.5 ${selectedGenerationType === 'all' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setSelectedGenerationType('image2image')}>
                      <Image className={`h-3.5 w-3.5 ${selectedGenerationType === 'image2image' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setSelectedGenerationType('text2image')}>
                      <Type className={`h-3.5 w-3.5 ${selectedGenerationType === 'text2image' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setSelectedGenerationType('image2video')}>
                      <Video className={`h-3.5 w-3.5 ${selectedGenerationType === 'image2video' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setSelectedGenerationType('text2video')}>
                      <Video className={`h-3.5 w-3.5 ${selectedGenerationType === 'text2video' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setSelectedGenerationType('morphing')}>
                      <Shuffle className={`h-3.5 w-3.5 ${selectedGenerationType === 'morphing' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </Button>
                  </div>
                  
                  {/* Row 2: Dimensions + Generation Modes */}
                  <div className="flex justify-center gap-2">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSelectedSize('square')}>
                      <Square className={`h-4 w-4 ${selectedSize === 'square' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSelectedSize('wide')}>
                      <RectangleHorizontal className={`h-4 w-4 ${selectedSize === 'wide' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSelectedSize('tall')}>
                      <RectangleVertical className={`h-4 w-4 ${selectedSize === 'tall' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </Button>
                    
                    <div className="w-px h-6 bg-border mx-2 self-center" />
                    
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setGenerationMode('single')}>
                      <Square className={`h-4 w-4 ${generationMode === 'single' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setGenerationMode('batch')}>
                      <Grid3X3 className={`h-4 w-4 ${generationMode === 'batch' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </Button>
                  </div>
                  
                  {/* Row 3: Tab Navigation Icons */}
                  <div className="flex justify-center gap-2">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setActiveCarouselTab('search')}>
                      <FileText className={`h-4 w-4 ${activeCarouselTab === 'search' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setActiveCarouselTab('keydates')}>
                      <Calendar className={`h-4 w-4 ${activeCarouselTab === 'keydates' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setActiveCarouselTab('articles')}>
                      <FileText className={`h-4 w-4 ${activeCarouselTab === 'articles' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </Button>
                  </div>
                </div>
              )}

              <div className="relative">
                {/* Desktop: Carousel with filters */}
                {!isMobile && (
                  <div className="flex gap-6">
                    <div className="flex-1">
                      <Card>
                        <CardContent className="pt-6">
                          <div className="flex gap-6">
                            <div className="flex-1">
                              <PromptPreviewCarousel
                                onPromptSelect={handlePromptSelect}
                                selectedPromptId={selectedPromptId}
                                selectedGenerationType={selectedGenerationType}
                              />
                            </div>
                            
                            {/* Filter Buttons - Right Side */}
                            <div className="w-48 flex-shrink-0 space-y-2">
                              <Button
                                variant={selectedGenerationType === 'all' ? "default" : "outline"}
                                size="sm"
                                className="w-full h-9 flex items-center gap-2 justify-start"
                                onClick={() => setSelectedGenerationType('all')}
                              >
                                <Sparkles className="h-4 w-4" />
                                <span className="font-medium">All Types</span>
                              </Button>
                              <Button
                                variant={selectedGenerationType === 'image2image' ? "default" : "outline"}
                                size="sm"
                                className="w-full h-9 flex items-center gap-2 justify-start"
                                onClick={() => setSelectedGenerationType('image2image')}
                              >
                                <Image className="h-4 w-4" />
                                <span className="font-medium">Image to Image</span>
                              </Button>
                              <Button
                                variant={selectedGenerationType === 'text2image' ? "default" : "outline"}
                                size="sm"
                                className="w-full h-9 flex items-center gap-2 justify-start"
                                onClick={() => setSelectedGenerationType('text2image')}
                              >
                                <Type className="h-4 w-4" />
                                <span className="font-medium">Text to Image</span>
                              </Button>
                              <Button
                                variant={selectedGenerationType === 'image2video' ? "default" : "outline"}
                                size="sm"
                                className="w-full h-9 flex items-center gap-2 justify-start"
                                onClick={() => setSelectedGenerationType('image2video')}
                              >
                                <Video className="h-4 w-4" />
                                <span className="font-medium">Image to Video</span>
                              </Button>
                              <Button
                                variant={selectedGenerationType === 'text2video' ? "default" : "outline"}
                                size="sm"
                                className="w-full h-9 flex items-center gap-2 justify-start"
                                onClick={() => setSelectedGenerationType('text2video')}
                              >
                                <Video className="h-4 w-4" />
                                <span className="font-medium">Text to Video</span>
                              </Button>
                              <Button
                                variant={selectedGenerationType === 'morphing' ? "default" : "outline"}
                                size="sm"
                                className="w-full h-9 flex items-center gap-2 justify-start"
                                onClick={() => setSelectedGenerationType('morphing')}
                              >
                                <Shuffle className="h-4 w-4" />
                                <span className="font-medium">Morphing</span>
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}

                {/* Mobile: Simple Carousel */}
                {isMobile && (
                  <div className="mb-1">
                    <PromptPreviewCarousel
                      onPromptSelect={handlePromptSelect}
                      selectedPromptId={selectedPromptId}
                      selectedGenerationType={selectedGenerationType}
                      activeTab={activeCarouselTab}
                      onTabChange={setActiveCarouselTab}
                    />
                  </div>
                )}

                <div className={`relative ${!isMobile ? 'mt-6' : ''}`}>
                  {/* Prompt Input Section */}
                  <div className={`relative ${!isMobile ? 'z-20 -mt-48' : ''}`}>
                    <Card className="bg-background border shadow-lg">
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          {/* Conditional Layout: Stack vertically on mobile for morphing */}
                          {isMobile && selectedGenerationType === 'morphing' ? (
                            <>
                              {/* Images First on Mobile Morphing */}
                              <div className="flex justify-center">
                                <div className="flex items-center gap-3">
                                  <button
                                    onClick={() => {
                                      setUploadingFrame('first');
                                      setIsImageModalOpen(true);
                                    }}
                                    className="w-24 h-24 border-2 border-dashed rounded-lg hover:border-primary transition-colors relative overflow-hidden border-muted-foreground/30"
                                  >
                                    {firstFrameFiles.length > 0 ? (
                                      <img 
                                        src={URL.createObjectURL(firstFrameFiles[0])} 
                                        alt="Start frame"
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="flex flex-col items-center justify-center h-full p-2">
                                        <img src={uploadIcon} alt="Upload" className="w-8 h-8 mb-1" />
                                        <div className="text-xs font-medium">Start</div>
                                      </div>
                                    )}
                                  </button>
                                  
                                  <ArrowRight className="h-6 w-6 text-muted-foreground" />
                                  
                                  <button
                                    onClick={() => {
                                      setUploadingFrame('last');
                                      setIsImageModalOpen(true);
                                    }}
                                    className="w-24 h-24 border-2 border-dashed rounded-lg hover:border-primary transition-colors relative overflow-hidden border-muted-foreground/30"
                                  >
                                    {lastFrameFiles.length > 0 ? (
                                      <img 
                                        src={URL.createObjectURL(lastFrameFiles[0])} 
                                        alt="End frame"
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="flex flex-col items-center justify-center h-full p-2">
                                        <img src={uploadIcon} alt="Upload" className="w-8 h-8 mb-1" />
                                        <div className="text-xs font-medium">End</div>
                                      </div>
                                    )}
                                  </button>
                                </div>
                              </div>
                              
                              {/* Prompt Below on Mobile Morphing */}
                              <div className="relative">
                                <div className="absolute top-2 right-2 z-10">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowShortcodePopup(true)}
                                    className="h-8 w-8 p-0 hover:bg-muted"
                                    title="View available shortcodes"
                                  >
                                    <Code className="h-4 w-4" />
                                  </Button>
                                </div>
                                
                                <textarea
                                  placeholder="Describe the image you want to generate"
                                  value={customPrompt}
                                  onChange={(e) => handleCustomPromptChange(e.target.value)}
                                  className="w-full min-h-[120px] p-3 pr-12 border border-input rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-ring bg-background"
                                />
                              </div>
                            </>
                          ) : (
                            // Standard Horizontal Layout for Desktop or Non-Morphing Mobile
                            <div className="flex gap-4">
                              {selectedGenerationType === 'morphing' ? (
                                // Morphing: Two upload buttons with arrow (Desktop)
                                <div className="flex items-center gap-3">
                                  <button
                                    onClick={() => {
                                      setUploadingFrame('first');
                                      setIsImageModalOpen(true);
                                    }}
                                    className={`${isMobile ? 'w-24 h-24' : 'w-32 h-32'} border-2 border-dashed rounded-lg hover:border-primary transition-colors relative overflow-hidden ${
                                      firstFrameFiles.length > 0 ? 'border-primary' : 'border-muted-foreground/30'
                                    }`}
                                  >
                                    {firstFrameFiles.length > 0 ? (
                                      <img 
                                        src={URL.createObjectURL(firstFrameFiles[0])} 
                                        alt="Start frame"
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="flex flex-col items-center justify-center h-full p-2">
                                        <img src={uploadIcon} alt="Upload" className="w-8 h-8 mb-1" />
                                        <div className="text-xs font-medium">Start</div>
                                      </div>
                                    )}
                                  </button>
                                  
                                  <ArrowRight className="h-6 w-6 text-muted-foreground" />
                                  
                                  <button
                                    onClick={() => {
                                      setUploadingFrame('last');
                                      setIsImageModalOpen(true);
                                    }}
                                    className={`${isMobile ? 'w-24 h-24' : 'w-32 h-32'} border-2 border-dashed rounded-lg hover:border-primary transition-colors relative overflow-hidden ${
                                      lastFrameFiles.length > 0 ? 'border-primary' : 'border-muted-foreground/30'
                                    }`}
                                  >
                                    {lastFrameFiles.length > 0 ? (
                                      <img 
                                        src={URL.createObjectURL(lastFrameFiles[0])} 
                                        alt="End frame"
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="flex flex-col items-center justify-center h-full p-2">
                                        <img src={uploadIcon} alt="Upload" className="w-8 h-8 mb-1" />
                                        <div className="text-xs font-medium">End</div>
                                      </div>
                                    )}
                                  </button>
                                </div>
                              ) : (
                                // Other types: Single upload button
                                <div 
                                  className={`${isMobile ? 'w-12 h-12' : 'w-32 h-32'} border-2 border-dashed border-muted-foreground/30 rounded-lg flex items-center justify-center bg-muted/20 cursor-pointer hover:border-primary transition-colors`}
                                  onClick={() => setIsImageModalOpen(true)}
                                >
                                  {selectedFiles.length > 0 ? (
                                    <div className="relative w-full h-full">
                                      <img 
                                        src={URL.createObjectURL(selectedFiles[0])} 
                                        alt="First selected" 
                                        className="w-full h-full object-cover rounded-md"
                                      />
                                      {selectedFiles.length > 1 && (
                                        <div className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-4 h-4 flex items-center justify-center">
                                          +{selectedFiles.length - 1}
                                        </div>
                                      )}
                                      {isMobile && (
                                        <div className="absolute bottom-0 left-0 right-0">
                                          <div className="text-xs text-white bg-black/70 rounded-b px-1 text-center">
                                            {selectedFiles.length}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="text-center text-muted-foreground">
                                      <img 
                                        src={uploadIcon} 
                                        alt="Upload" 
                                        className={`${isMobile ? 'w-8 h-8' : 'w-8 h-8'} mx-auto`}
                                      />
                                      {!isMobile && <div className="text-xs mt-1">Upload</div>}
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Prompt Text Area */}
                              <div className="flex-1 relative">
                                <div className="absolute top-2 right-2 z-10">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowShortcodePopup(true)}
                                    className="h-8 w-8 p-0 hover:bg-muted"
                                    title="View available shortcodes"
                                  >
                                    <Code className="h-4 w-4" />
                                  </Button>
                                </div>
                                
                                <textarea
                                  placeholder="Describe the image you want to generate"
                                  value={customPrompt}
                                  onChange={(e) => handleCustomPromptChange(e.target.value)}
                                  className={`w-full ${isMobile ? 'min-h-[60px]' : 'min-h-[120px]'} p-3 pr-12 border border-input rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-ring bg-background`}
                                />
                              </div>
                            </div>
                          )}

                          {/* Desktop: Size dropdown and Model selection */}
                          {!isMobile && (
                            <div className="flex items-center gap-4">
                              {/* Visual Size Dropdown */}
                              <div className="flex-shrink-0">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="outline" className="gap-2" disabled={sizesLoading}>
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                      </svg>
                                      {selectedVisualSize ? `${selectedVisualSize.platform} - ${selectedVisualSize.format}` : 'Select Size'}
                                      <ChevronDown className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="w-80 max-h-80 overflow-y-auto">
                                    {sizes.map((size) => (
                                      <DropdownMenuItem
                                        key={size.id}
                                        onClick={() => setSelectedVisualSize(size)}
                                        className="flex flex-col items-start p-3 cursor-pointer"
                                      >
                                        <div className="flex items-center justify-between w-full">
                                          <span className="font-medium">{size.platform} - {size.format}</span>
                                          {size.aspect_ratio && (
                                            <Badge variant="secondary" className="text-xs">
                                              {size.aspect_ratio}
                                            </Badge>
                                          )}
                                        </div>
                                        {size.recommended_px && (
                                          <span className="text-xs text-muted-foreground">
                                            Recommended: {size.recommended_px}
                                          </span>
                                        )}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                              
                              {/* AI Model Badges */}
                              <div className="flex flex-wrap gap-2 ml-4">
                                {Object.keys(validModelsForTab).length === 0 ? (
                                  <div className="text-sm text-muted-foreground">
                                    No models provisioned yet. Contact the Admin
                                  </div>
                                ) : (
                                  sortPlatforms(Object.entries(validModelsForTab)
                                    .filter(([platform, models]) => models.length > 0))
                                    .map(([platform, models]) => 
                                    models.map((model: any) => {
                                      const platformForComparison = model.provider || platform;
                                      const isSelected = selectedModels.some(m => m.platform === platformForComparison && m.model === model.id);
                                      
                                      return (
                                        <Badge
                                          key={model.id}
                                          variant={isSelected ? "default" : "outline"}
                                          className="cursor-pointer hover:bg-primary/20 max-w-[150px] truncate text-xs"
                                          onClick={() => handleModelSelect(platform, model.id)}
                                        >
                                          {isSelected && <Check className="h-3 w-3 mr-1 flex-shrink-0" />}
                                          {model.name}
                                        </Badge>
                                      );
                                    })
                                  ).flat()
                                )}
                              </div>
                            </div>
                          )}

                          {/* Mobile: Visual Size Dropdown */}
                          {isMobile && (
                            <div className="space-y-3">
                              <div className="text-xs text-muted-foreground font-medium">Select Visual Size</div>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="outline" className="w-full gap-2" disabled={sizesLoading}>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                    </svg>
                                    {selectedVisualSize ? `${selectedVisualSize.platform} - ${selectedVisualSize.format}` : 'Select Visual Size'}
                                    <ChevronDown className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="w-80 max-h-80 overflow-y-auto">
                                  {sizes.map((size) => (
                                    <DropdownMenuItem
                                      key={size.id}
                                      onClick={() => setSelectedVisualSize(size)}
                                      className="flex flex-col items-start p-3 cursor-pointer"
                                    >
                                      <div className="flex items-center justify-between w-full">
                                        <span className="font-medium">{size.platform} - {size.format}</span>
                                        {size.aspect_ratio && (
                                          <Badge variant="secondary" className="text-xs">
                                            {size.aspect_ratio}
                                          </Badge>
                                        )}
                                      </div>
                                      {size.recommended_px && (
                                        <span className="text-xs text-muted-foreground">
                                          Recommended: {size.recommended_px}
                                        </span>
                                      )}
                                      {size.notes && (
                                        <span className="text-xs text-muted-foreground mt-1">
                                          {size.notes}
                                        </span>
                                      )}
                                    </DropdownMenuItem>
                                  ))}
                                  {sizes.length === 0 && !sizesLoading && (
                                    <DropdownMenuItem disabled>
                                      No visual sizes available
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          )}

                          {/* Mobile: AI Model Selection */}
                          {isMobile && (
                            <div className="space-y-3">
                              <div className="text-xs text-muted-foreground font-medium">Select Models</div>
                              <div className="flex flex-wrap gap-2">
                                {Object.keys(validModelsForTab).length === 0 ? (
                                  <div className="text-sm text-muted-foreground">
                                    No models provisioned yet. Contact the Admin
                                  </div>
                                ) : (
                                  sortPlatforms(Object.entries(validModelsForTab)
                                    .filter(([platform, models]) => models.length > 0))
                                    .map(([platform, models]) => 
                                    models.map((model: any) => {
                                      const platformForComparison = model.provider || platform;
                                      const isSelected = selectedModels.some(m => m.platform === platformForComparison && m.model === model.id);
                                      
                                      return (
                                        <Badge
                                          key={model.id}
                                          variant={isSelected ? "default" : "outline"}
                                          className="cursor-pointer hover:bg-primary/20 max-w-[150px] truncate text-xs"
                                          onClick={() => handleModelSelect(platform, model.id)}
                                        >
                                          {isSelected && <Check className="h-3 w-3 mr-1 flex-shrink-0" />}
                                          {model.name}
                                        </Badge>
                                      );
                                    })
                                  ).flat()
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Desktop Generation Mode Controls */}
                        {!isMobile && (
                          <div className="mt-4 px-2">
                            <div className="text-xs text-muted-foreground mb-2 font-medium flex items-center gap-2">
                              <Images className="h-4 w-4" /> Generation Mode
                            </div>
                            <div className="grid grid-cols-4 gap-2 mb-3">
                              <Button
                                variant={generationMode === 'single' ? 'default' : 'outline'}
                                size="sm"
                                className="flex items-center gap-2 justify-start"
                                onClick={() => setGenerationMode('single')}
                              >
                                <Square className="h-4 w-4" /> 
                                <span>Single</span>
                              </Button>
                              <Button
                                variant={generationMode === 'batch' ? 'default' : 'outline'}
                                size="sm"
                                className="flex items-center gap-2 justify-start"
                                onClick={() => setGenerationMode('batch')}
                              >
                                <Grid3X3 className="h-4 w-4" /> 
                                <span>Batch</span>
                              </Button>
                            </div>
                            
                            {generationMode === 'batch' && (
                              <div className="flex flex-wrap items-center gap-2 mt-2">
                                <span className="text-xs font-medium">Max Images:</span>
                                {[2,3,4,6,8].map(num => (
                                  <Button key={num} variant={maxImages === num ? 'default' : 'outline'} size="sm" onClick={() => setMaxImages(num)} className="w-8 h-8 p-0">
                                    {num}
                                  </Button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Mobile: Max Images for generation modes */}
                        {isMobile && generationMode !== 'single' && (
                          <div className="space-y-3">
                            <div className="text-xs text-muted-foreground font-medium">Max Images: {maxImages}</div>
                            <div className="flex gap-2 justify-center">
                              {[2,3,4,6,8].map(num => (
                                <Button key={num} variant={maxImages === num ? 'default' : 'outline'} size="sm" onClick={() => setMaxImages(num)} className="w-8 h-8 p-0">
                                  {num}
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Floating Generate Button */}
                        <div className="absolute bottom-4 right-4">
                          <Button 
                            className="bg-primary hover:bg-primary/90 rounded-full w-12 h-12 p-0 shadow-lg relative"
                            disabled={selectedModels.length === 0 || isGenerating}
                            onClick={handleGenerate}
                          >
                            <Send className="w-5 h-5" />
                            {selectedModels.length > 0 && (
                              <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                {selectedModels.length}
                              </div>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Progress */}
                  {isGenerating && (
                    <div className="mt-6">
                      <Card>
                        <CardContent className="p-6">
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>Generating with {selectedModels.length} model{selectedModels.length !== 1 ? 's' : ''}...</span>
                              <span>{progress}%</span>
                            </div>
                            <Progress value={progress} className="w-full" />
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                  
                  {/* Results Section */}
                  {results.length > 0 && (
                    <div className="mt-6">
                      <Card>
                        <CardHeader>
                          <CardTitle>Generated Results</CardTitle>
                          <CardDescription>
                            Your generated images will appear here
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <GenerationResultsGrid
                            results={results}
                            onPreview={(url) => {
                              setPreviewImageUrl(url);
                              setIsPreviewVideo(url.includes('.mp4') || url.includes('video'));
                              setIsPreviewModalOpen(true);
                            }}
                            onDownload={(imageUrl, filename) => {
                              const link = document.createElement('a');
                              link.href = imageUrl;
                              link.download = filename;
                              link.click();
                            }}
                            onUseAsInput={(imageUrl) => {
                              if (imageUrl) {
                                fetch(imageUrl)
                                  .then(res => res.blob())
                                  .then(blob => {
                                    const file = new File([blob], 'generated-input.png', { type: 'image/png' });
                                    setSelectedFiles([...selectedFiles, file]);
                                    toast.success('Image added as input');
                                  })
                                  .catch(error => {
                                    console.error('Error converting image to file:', error);
                                    toast.error('Failed to add image as input');
                                  });
                              }
                            }}
                            onSetAsPreview={handleSetAsPreview}
                            onDelete={(result) => {
                              setResults(prev => prev.filter(r => r.id !== result.id));
                              toast.success('Generation result deleted');
                            }}
                          />
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </ScrollArea>
        </DrawerContent>
      </Drawer>

      {/* Image Selection Modal */}
      <ImageSelectionModal 
        isOpen={isImageModalOpen}
        onClose={() => {
          setIsImageModalOpen(false);
          setUploadingFrame(null);
        }}
        selectedFiles={selectedGenerationType === 'morphing' && uploadingFrame ? 
          (uploadingFrame === 'first' ? firstFrameFiles : lastFrameFiles) : 
          selectedFiles
        }
        onFilesSelect={(files: File[]) => {
          if (selectedGenerationType === 'morphing' && uploadingFrame) {
            const limitedFiles = files.slice(0, 1);
            if (uploadingFrame === 'first') {
              setFirstFrameFiles(limitedFiles);
            } else if (uploadingFrame === 'last') {
              setLastFrameFiles(limitedFiles);
            }
            setUploadingFrame(null);
          } else {
            setSelectedFiles(files);
          }
          setIsImageModalOpen(false);
        }}
      />

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => {
          setIsPreviewModalOpen(false);
          setPreviewImageUrl(null);
          setIsPreviewVideo(false);
        }}
        imageUrl={previewImageUrl}
        isVideo={isPreviewVideo}
      />

      {/* Shortcode Popup */}
      <ShortcodePopup 
        open={showShortcodePopup} 
        onOpenChange={setShowShortcodePopup} 
      />
    </>
  );
};
